import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './LandingPage.css';
import ChampionSearch from './ChampionSearch';
import ChampionGrid from './ChampionGrid';
import SummonerSearchBar from '../pages/Summoner/components/SummonerSearchBar';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const isProduction = process.env.REACT_APP_DEMO_MODE === 'true' || process.env.NODE_ENV === 'production';
  const handleSummonerSearch = (region: string, gameName: string, tagLine: string) => {
    navigate(`/summoner/${region}/${encodeURIComponent(gameName)}/${encodeURIComponent(tagLine)}`);
  };

  const handleChampionSelect = (championName: string) => {
    if (championName.trim() === '') {
      alert('Please enter a champion name');
      return;
    }
    navigate(`/champions/${championName}`);
  };

  return (
    <div className="landing-page-container">
      <Helmet>
        <title>LoL Calculator – League of Legends Damage Calculator & Sandbox</title>
        <meta name="description" content="Free League of Legends damage calculator and sandbox. Simulate champion matchups, compare item builds, analyze combos, and find optimal rune setups for every champion in LoL." />
        <link rel="canonical" href="https://lolcalculator.gg" />
      </Helmet>
      <div className="main-content">
        <div className="logo-title-container">
          <img 
            src="/logov6.png" 
            alt="LoL Calculator" 
            className="logo-image"
          />
          <h1 className="logo-title">LoL Calculator</h1>
        </div>
        
        <div className="champion-search-section">
          <ChampionSearch 
            onSelectChampion={handleChampionSelect}
            placeholder="Search for a champion..."
          />
          <ChampionGrid onSelectChampion={handleChampionSelect} />
          <p className="section-description">
            Look up builds, runes, and matchup data for any champion
          </p>
        </div>

        <div className="landing-divider"></div>

        <div className="tools-section">
          <button
            onClick={() => navigate('/damage-calculator')}
            className="tool-button damage-calculator-button"
          >
            Damage Calculator Sandbox
          </button>
          <p className="section-description">
            Test custom combos, builds and interactions between any two champions</p>

          {/* Dev-only tools — hidden in production */}
          {!isProduction && (
            <>
              <div className="landing-divider"></div>

              <div className="summoner-search-section">
                <SummonerSearchBar onSearch={handleSummonerSearch} />
                <p className="section-description">
                  Search for a summoner to view match history and ranked stats
                </p>
              </div>

              <button
                onClick={() => navigate('/team-comp')}
                className="tool-button team-comp-button"
              >
                Team Comp Builder
              </button>
              <p className="section-description">
                Build full team compositions and compare champions across all roles</p>

              <button
                onClick={() => navigate('/macro-analysis')}
                className="tool-button macro-analysis-button"
              >
                Macro Analysis MVP
              </button>
              <p className="section-description">
                Run the offline macro pipeline and inspect timeline scores with validation checks
              </p>

              <button
                onClick={() => navigate('/combo-practice')}
                className="tool-button combo-practice-button"
              >
                Combo Input Practice
              </button>

              <button
                onClick={() => navigate('/teamfight-simulator')}
                className="tool-button teamfight-simulator-button"
              >
                Teamfight Simulator
              </button>

              <button
                onClick={() => navigate('/farming-calculator')}
                className="tool-button gold-calculator-button"
              >
                Gold Calculator
              </button>

              <button
                onClick={() => navigate('/xp-calculator')}
                className="tool-button xp-calculator-button"
              >
                XP Calculator
              </button>

              <button
                onClick={() => navigate('/jungle-simulator')}
                className="tool-button jungle-simulator-button"
              >
                Jungle Simulator
              </button>

              <button
                onClick={() => navigate('/map-viewer')}
                className="tool-button map-viewer-button"
              >
                Map Pathfinder
              </button>

              <button
                onClick={() => navigate('/rift-simulator')}
                className="tool-button rift-simulator-button"
              >
                Rift Simulator
              </button>

              <button
                onClick={() => navigate('/map-editor')}
                className="tool-button map-editor-button"
              >
                Map Editor
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
